import { useIonRouter } from '@ionic/react';
import { useQuery } from '@tanstack/react-query';
import {
  Permission,
  ProductBehavior,
  ProductsPublicApi,
  ProfilesPublicApi,
} from '@up2stay/up2promo-api-client';
import type { Profile } from '@up2stay/up2promo-api-client';
import {
  getProductKindLabel,
  resolveEffectiveProductType,
} from '@up2stay/up2promo-commons/products';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  cn,
  CopyButton,
  resolveProfileTokens,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@up2stay/up2promo-ui';
import { LoaderCircle } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { BusinessCardExchangeDialog } from '@/core/components/profiles/business-card-exchange-dialog';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { usePermissions } from '@/core/hooks/use-permissions';
import { resolveLinkResolveSetupPath } from '@/core/lib/build-products-setup-path';
import { getAbsoluteFileUrl } from '@/core/lib/file-url';
import {
  classifyLinkResolve,
  type LinkResolveDestination,
} from '@/core/lib/link-resolve-classify';
import {
  classifyNativeLink,
  withScannedProductType,
} from '@/core/lib/native-deep-link';
import { resolveNotificationDeepLink } from '@/core/lib/notification-deep-link';
import {
  openExternalUrl,
  openInSystemBrowser,
} from '@/core/lib/open-external-url';
import { resolveProfilePublicHash } from '@/core/lib/resolve-profile-public-hash';
import { resolveProductHash } from '@/core/lib/product-hash-resolution';
import { scanExchangeDebug } from '@/core/lib/scan-exchange-debug';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths, TABS_PREFIX } from '@/core/router/app-paths';
import {
  isAppSessionActive,
  persistAuthReturnUrl,
} from '@/core/router/auth-route-gate';
import { useAuthStore } from '@/core/store/auth.store';
import {
  closeLinkResolve,
  useLinkResolveStore,
} from '@/core/store/link-resolve.store';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';
import type { ProductsDict } from '@/routes/app/products/products.types';

import { env } from '@/core/env';

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0] ?? ''}${parts[1]![0] ?? ''}`.toUpperCase();
}

function profileViewerUrl(workspaceUsername: string, username: string): string {
  const base = env.VITE_PROFILE_VIEWER_DOMAIN;
  return `${base.replace(/\/$/, '')}/${encodeURIComponent(workspaceUsername)}/${encodeURIComponent(username)}`;
}

function titleForDestination(
  destination: LinkResolveDestination,
  dict: Record<string, string | undefined>,
  productsDict?: ProductsDict,
): string {
  switch (destination.kind) {
    case 'profile_card':
      return dict.kindBusinessCard ?? 'Business card';
    case 'owned_product':
      return dict.kindYourProduct ?? 'Your product';
    case 'teammate_product':
      return dict.kindTeammateProduct ?? 'Team product';
    case 'needs_setup':
      return dict.kindNeedsSetup ?? 'Needs setup';
    case 'external':
      return dict.kindExternal ?? 'External link';
    case 'visitor_destination': {
      const kindLabel = getProductKindLabel(
        resolveEffectiveProductType(
          destination.productType,
          destination.mappingType ?? ProductBehavior.URL,
        ),
        productsDict?.edit,
      );
      if (kindLabel && kindLabel !== '—') return kindLabel;
      return dict.kindVisitorDestination ?? 'Product';
    }
    case 'app_path':
      return dict.kindApp ?? 'Open in app';
    case 'error_unavailable':
    case 'error_generic':
      return dict.kindError ?? 'Could not open';
    default:
      return dict.title ?? 'Link';
  }
}

/**
 * App-wide dialog for Cap QR / Universal Link resolution (also works on auth screens).
 */
export function LinkResolveDialog() {
  const dictionary = useDictionary();
  const dict = dictionary.linkResolve ?? {};
  const deepLinkDict = dictionary.deepLink;
  const productsDict = dictionary.products as ProductsDict | undefined;
  const open = useLinkResolveStore((s) => s.open);
  const phase = useLinkResolveStore((s) => s.phase);
  const request = useLinkResolveStore((s) => s.request);
  const destination = useLinkResolveStore((s) => s.destination);
  const errorMessage = useLinkResolveStore((s) => s.errorMessage);
  const setResolving = useLinkResolveStore((s) => s.setResolving);
  const setResolved = useLinkResolveStore((s) => s.setResolved);
  const setError = useLinkResolveStore((s) => s.setError);

  const authStatus = useAuthStore((s) => s.authStatus);
  const user = useAuthStore((s) => s.user);
  const currentWorkspace = useCurrentWorkspace();
  const { can } = usePermissions();
  const canManageProducts = can(Permission.PRODUCTS_MANAGE);
  const { navigateShellDeepLink } = useNavigateShell();
  const router = useIonRouter();
  const isSignedIn = isAppSessionActive(authStatus);

  const [exchangeOpen, setExchangeOpen] = useState(false);
  const [exchangeTarget, setExchangeTarget] = useState<{
    profileId: string;
    title?: string;
  } | null>(null);

  useEffect(() => {
    if (!open || !request) return;
    let cancelled = false;

    const run = async () => {
      setResolving();
      try {
        if (request.source === 'profile') {
          const url = profileViewerUrl(
            request.workspaceUsername,
            request.username,
          );
          const next = classifyLinkResolve({
            hash: request.scanHash ?? null,
            resolution: null,
            directProfile: {
              workspaceUsername: request.workspaceUsername,
              username: request.username,
              profileUrl: url,
            },
          });
          if (!cancelled) setResolved(next);
          return;
        }

        if (request.source === 'profile-hash') {
          const resolved = await resolveProfilePublicHash(
            request.hash,
            request.src,
          );
          if (!resolved) {
            throw new Error('profile hash not found');
          }
          const url = profileViewerUrl(
            resolved.workspaceUsername,
            resolved.username,
          );
          const next = classifyLinkResolve({
            hash: request.hash,
            resolution: null,
            directProfile: {
              workspaceUsername: resolved.workspaceUsername,
              username: resolved.username,
              profileUrl: url,
            },
          });
          scanExchangeDebug('link-resolve.classified', {
            hash: request.hash,
            kind: next.kind,
            source: 'profile-hash',
          });
          if (!cancelled) setResolved(next);
          return;
        }

        const response = await ProductsPublicApi.resolveTargetUrl({
          path: { hash: request.hash },
        });
        const resolution = response.data ?? null;

        let ownership = null;
        if (
          isAppSessionActive(authStatus) &&
          currentWorkspace?.id &&
          request.hash
        ) {
          ownership = await resolveProductHash(
            request.hash,
            currentWorkspace.id,
          );
        }

        const next = classifyLinkResolve({
          hash: request.hash,
          resolution,
          ownership,
          hintProductType:
            request.source === 'hash' ? request.productType : undefined,
        });
        scanExchangeDebug('link-resolve.classified', {
          hash: request.hash,
          kind: next.kind,
          mappingType: next.mappingType,
          productType: next.productType,
        });
        if (!cancelled) setResolved(next);
      } catch (error) {
        scanExchangeDebug('link-resolve.failed', {
          request,
          error:
            error instanceof Error
              ? { name: error.name, message: error.message }
              : String(error),
        });
        if (!cancelled) {
          setError(deepLinkDict?.failed ?? 'This link could not be opened.');
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    request,
    authStatus,
    currentWorkspace?.id,
    user?.id,
    setResolving,
    setResolved,
    setError,
    deepLinkDict?.failed,
  ]);

  const profileKey = destination?.profile
    ? `${destination.profile.workspaceUsername}/${destination.profile.username}`
    : null;

  const profileQuery = useQuery({
    queryKey: ['link-resolve-profile', profileKey],
    queryFn: async (): Promise<Profile> => {
      if (!destination?.profile) throw new Error('missing');
      const response = await ProfilesPublicApi.findByWorkspaceAndUsername({
        path: {
          workspaceUsername: destination.profile.workspaceUsername,
          username: destination.profile.username,
        },
      });
      if (!response.data) throw new Error('profileNotFound');
      return response.data;
    },
    enabled:
      open && destination?.kind === 'profile_card' && Boolean(profileKey),
    retry: false,
  });

  const profile = profileQuery.data;
  const canExchange =
    Boolean(user) &&
    profile?.user !== user?.id &&
    profile?.leadForm?.enabled === true;

  const displayName =
    profile?.title?.trim() ||
    destination?.profile?.username ||
    dict.unknownProfile ||
    'Profile';

  const photoUrl = useMemo(() => {
    const path =
      profile?.showProfileImage !== false
        ? profile?.profileImage?.path?.trim()
        : undefined;
    return path ? getAbsoluteFileUrl(path) : null;
  }, [profile]);

  const avatarFallbackStyle = useMemo(() => {
    const tokens = resolveProfileTokens(
      profile?.styling?.accentColor ?? null,
      'light',
    );
    return {
      backgroundColor: tokens.accentFill,
      color: tokens.accentText,
    };
  }, [profile?.styling?.accentColor]);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      // Keep exchange open if it already replaced this sheet.
      if (!exchangeOpen) {
        setExchangeTarget(null);
      }
      closeLinkResolve();
    }
  };

  const openExchange = () => {
    if (!profile?.id) return;
    setExchangeTarget({
      profileId: profile.id,
      title: profile.title ?? destination?.profile?.username,
    });
    closeLinkResolve();
    setExchangeOpen(true);
  };

  const navigateInApp = (path: string) => {
    closeLinkResolve();
    const resolved = resolveNotificationDeepLink(path);
    if (resolved.startsWith(TABS_PREFIX)) {
      // Cold entry seeds the detail parent (e.g. products list) so iOS
      // swipe-back matches PageHeader backHref — plain navigateShell would
      // root-replace onto the detail with an empty stack.
      void navigateShellDeepLink(resolved);
    } else {
      router.push(resolved, 'root', 'replace');
    }
  };

  const goToLogin = (returnPath: string) => {
    persistAuthReturnUrl(returnPath);
    closeLinkResolve();
    router.push(
      isCapacitorNative() ? '/auth/welcome' : '/auth',
      'root',
      'replace',
    );
  };

  const openProfileInAppBrowser = async (url: string) => {
    await openExternalUrl(url);
  };

  /** Public product destinations (review, IG, …) → OS browser, not Cap sheet. */
  const openPublicDestination = async (url: string) => {
    const hash = destination?.hash;
    const withType =
      hash && destination?.productType
        ? withScannedProductType(url, hash, destination.productType)
        : url;
    await openInSystemBrowser(withType);
  };

  const setupPathForDestination = (
    dest: NonNullable<typeof destination>,
  ): string =>
    resolveLinkResolveSetupPath({
      hash: dest.hash ?? '',
      productType: dest.productType,
      mappingType: dest.mappingType,
      appPath: dest.appPath,
      // Unsigned: keep BC on link-card (member-safe). Managers signed in with
      // PRODUCTS_MANAGE get workspace setup + profile owner picker.
      canManageProducts: isSignedIn && canManageProducts,
    });

  const primaryActions = () => {
    if (!destination) return null;

    switch (destination.kind) {
      case 'profile_card': {
        const viewUrl =
          destination.targetUrl ||
          (destination.profile
            ? profileViewerUrl(
                destination.profile.workspaceUsername,
                destination.profile.username,
              )
            : null);
        const exchangeReturn = destination.profile
          ? shellPaths.leadsPublicProfilePath(
              destination.profile.workspaceUsername,
              destination.profile.username,
            ) +
            (destination.hash
              ? `?scanHash=${encodeURIComponent(destination.hash)}`
              : '')
          : shellPaths.home;
        return (
          <>
            {canExchange && profile?.id ? (
              <Button type="button" className="w-full" onClick={openExchange}>
                {dict.exchange ?? 'Exchange contact details'}
              </Button>
            ) : null}
            {!isSignedIn ? (
              <Button
                type="button"
                className="w-full"
                onClick={() => goToLogin(exchangeReturn)}
              >
                {dict.signInToExchange ?? 'Sign in to exchange'}
              </Button>
            ) : null}
            {destination.productId ? (
              <Button
                type="button"
                variant={canExchange || !isSignedIn ? 'outline' : 'primary'}
                className="w-full"
                onClick={() =>
                  navigateInApp(
                    shellPaths.moreProductDetail(destination.productId!),
                  )
                }
              >
                {dict.editProduct ?? 'Edit product'}
              </Button>
            ) : null}
            {viewUrl ? (
              <Button
                type="button"
                variant={
                  canExchange || !isSignedIn || destination.productId
                    ? 'outline'
                    : 'primary'
                }
                className="w-full"
                onClick={() => void openProfileInAppBrowser(viewUrl)}
              >
                {dict.viewProfile ?? 'View profile'}
              </Button>
            ) : null}
          </>
        );
      }
      case 'owned_product':
        return (
          <>
            {destination.targetUrl ? (
              <Button
                type="button"
                className="w-full"
                onClick={() =>
                  void openPublicDestination(destination.targetUrl!)
                }
              >
                {dict.openLive ?? 'Open live destination'}
              </Button>
            ) : null}
            {destination.productId ? (
              <Button
                type="button"
                variant={destination.targetUrl ? 'outline' : 'primary'}
                className="w-full"
                onClick={() =>
                  navigateInApp(
                    shellPaths.moreProductDetail(destination.productId!),
                  )
                }
              >
                {dict.editProduct ?? 'Edit product'}
              </Button>
            ) : null}
          </>
        );
      case 'teammate_product':
        return (
          <>
            {destination.targetUrl ? (
              <Button
                type="button"
                className="w-full"
                onClick={() =>
                  void openPublicDestination(destination.targetUrl!)
                }
              >
                {dict.openLive ?? 'Open live destination'}
              </Button>
            ) : null}
            {destination.productId ? (
              <Button
                type="button"
                variant={destination.targetUrl ? 'outline' : 'primary'}
                className="w-full"
                onClick={() =>
                  navigateInApp(
                    shellPaths.moreProductDetail(destination.productId!),
                  )
                }
              >
                {dict.editProduct ?? 'Edit product'}
              </Button>
            ) : null}
            <Button
              type="button"
              variant={
                destination.targetUrl || destination.productId
                  ? 'outline'
                  : 'primary'
              }
              className="w-full"
              onClick={() => navigateInApp(shellPaths.moreProducts)}
            >
              {dict.goToProducts ?? 'Go to products'}
            </Button>
          </>
        );
      case 'needs_setup': {
        const setupPath = setupPathForDestination(destination);
        const canOpenExternalSetup =
          destination.targetUrl &&
          (() => {
            try {
              return (
                classifyNativeLink(new URL(destination.targetUrl)).kind !==
                'app'
              );
            } catch {
              return true;
            }
          })();
        return (
          <>
            {isSignedIn ? (
              <Button
                type="button"
                className="w-full"
                onClick={() => navigateInApp(setupPath)}
              >
                {dict.continueSetup ?? 'Continue setup'}
              </Button>
            ) : (
              <Button
                type="button"
                className="w-full"
                onClick={() => goToLogin(setupPath)}
              >
                {dict.signInToSetup ?? 'Sign in to set up'}
              </Button>
            )}
            {canOpenExternalSetup ? (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() =>
                  void openPublicDestination(destination.targetUrl!)
                }
              >
                {dict.openInBrowser ?? 'Open in browser'}
              </Button>
            ) : null}
          </>
        );
      }
      case 'external':
      case 'visitor_destination':
        return (
          <>
            {destination.targetUrl ? (
              <Button
                type="button"
                className="w-full"
                onClick={() =>
                  void openPublicDestination(destination.targetUrl!)
                }
              >
                {dict.openExternal ?? 'Open'}
              </Button>
            ) : null}
            {destination.targetUrl ? (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(destination.targetUrl!);
                    toast.success(dict.copied ?? 'Copied');
                  } catch {
                    toast.error(dict.copyFailed ?? 'Could not copy');
                  }
                }}
              >
                {dict.copyLink ?? 'Copy link'}
              </Button>
            ) : null}
          </>
        );
      case 'app_path':
        if (!destination.appPath) return null;
        if (isSignedIn) {
          return (
            <Button
              type="button"
              className="w-full"
              onClick={() => navigateInApp(destination.appPath!)}
            >
              {dict.openInApp ?? 'Continue'}
            </Button>
          );
        }
        return (
          <>
            <Button
              type="button"
              className="w-full"
              onClick={() => goToLogin(destination.appPath!)}
            >
              {dict.signInToContinue ?? 'Sign in to continue'}
            </Button>
            {destination.targetUrl ? (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() =>
                  void openPublicDestination(destination.targetUrl!)
                }
              >
                {dict.openInBrowser ?? 'Open in browser'}
              </Button>
            ) : null}
          </>
        );
      case 'error_unavailable':
      case 'error_generic':
        // Casual open: no setup/claim CTA. Managers use Products → Connect.
        return null;
      default:
        return null;
    }
  };

  const descriptionText = (() => {
    if (phase === 'resolving') {
      return dict.resolvingDescription ?? 'Looking up this product…';
    }
    if (phase === 'error') {
      return errorMessage ?? deepLinkDict?.failed;
    }
    if (!destination) return null;
    switch (destination.kind) {
      case 'profile_card':
        return isSignedIn
          ? (dict.profileDescription ??
              'Share your details or open the full profile.')
          : (dict.profileGuestDescription ??
              'Sign in to exchange contact details, or open the public profile.');
      case 'owned_product':
        return (
          dict.ownedDescription ?? 'This product belongs to your workspace.'
        );
      case 'teammate_product':
        return (
          dict.teammateDescription ??
          'This product is assigned to a teammate in your workspace.'
        );
      case 'needs_setup':
        return isSignedIn
          ? (dict.setupDescription ?? 'This product is not fully set up yet.')
          : (dict.setupGuestDescription ?? 'Sign in to set up this product.');
      case 'external':
        return (
          dict.externalDescription ?? 'Open the destination outside the app.'
        );
      case 'visitor_destination':
        return destination.targetUrl
          ? (dict.visitorDescription ?? 'Open where this product leads.')
          : (dict.visitorUnavailableDescription ??
              'This product is already set up. Its destination isn’t available here.');
      case 'app_path':
        return isSignedIn
          ? null
          : (dict.appGuestDescription ??
              'Sign in to continue in the app, or open the link in a browser.');
      case 'error_unavailable':
        return isSignedIn
          ? (dict.unavailableDescription ??
              'The destination for this product is not ready.')
          : (dict.unavailableGuestDescription ??
              'This product’s destination isn’t ready yet.');
      default:
        return null;
    }
  })();

  const displayHash =
    destination?.hash ??
    (request?.source === 'hash' || request?.source === 'profile-hash'
      ? request.hash
      : (request?.scanHash ?? null)) ??
    null;

  return (
    <>
      <ResponsiveDialog open={open} onOpenChange={handleOpenChange}>
        <ResponsiveDialogContent
          className={cn(
            'gap-0 overflow-hidden sm:max-w-md',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          )}
        >
          <ResponsiveDialogHeader className="space-y-1 text-left">
            <ResponsiveDialogTitle>
              {phase === 'resolving'
                ? (dict.resolvingTitle ?? 'Resolving link…')
                : destination
                  ? titleForDestination(destination, dict, productsDict)
                  : (dict.title ?? 'Link')}
            </ResponsiveDialogTitle>
            {descriptionText ? (
              <ResponsiveDialogDescription>
                {descriptionText}
              </ResponsiveDialogDescription>
            ) : null}
          </ResponsiveDialogHeader>

          <div className="space-y-3 px-1 py-4">
            {phase === 'resolving' ? (
              <div className="flex flex-col items-center justify-center gap-3 py-8">
                <LoaderCircle
                  className="size-8 animate-spin text-primary"
                  aria-hidden
                />
                <p className="text-sm text-muted-foreground">
                  {dict.resolvingTitle ?? 'Resolving link…'}
                </p>
              </div>
            ) : null}

            {phase === 'resolved' && destination?.kind === 'profile_card' ? (
              <div className="flex flex-col items-center gap-3 rounded-2xl border border-border/60 bg-muted/30 px-4 py-6 text-center">
                <Avatar className="size-20 ring-2 ring-background shadow-md">
                  {photoUrl ? (
                    <AvatarImage src={photoUrl} alt={displayName} />
                  ) : null}
                  <AvatarFallback
                    className="text-lg font-semibold"
                    style={avatarFallbackStyle}
                  >
                    {initialsFromName(displayName)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 space-y-1">
                  <p className="truncate text-lg font-semibold tracking-tight">
                    {profileQuery.isLoading ? '…' : displayName}
                  </p>
                  {profile?.jobTitle ? (
                    <p className="text-sm text-muted-foreground">
                      {profile.jobTitle}
                    </p>
                  ) : null}
                  {profile?.company ? (
                    <p className="text-sm text-muted-foreground">
                      {profile.company}
                    </p>
                  ) : null}
                  {profile?.location ? (
                    <p className="text-xs text-muted-foreground/80">
                      {profile.location}
                    </p>
                  ) : null}
                </div>
              </div>
            ) : null}

            {displayHash ? (
              <div className="space-y-1.5">
                <div className="space-y-0.5 px-0.5">
                  <p className="text-xs font-medium text-foreground">
                    {dict.hashLabel ?? 'Product code'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {dict.hashHint ??
                      'Identifies this product — useful if you contact support.'}
                  </p>
                </div>
                <div className="flex items-center gap-2 rounded-xl border border-border/50 bg-muted/20 px-3 py-2.5">
                  <p className="min-w-0 flex-1 truncate font-mono text-sm text-foreground">
                    {displayHash}
                  </p>
                  <CopyButton
                    content={displayHash}
                    variant="outline"
                    size="sm"
                    className="size-8 shrink-0"
                    aria-label={dict.copyHash ?? 'Copy code'}
                  />
                </div>
              </div>
            ) : null}
          </div>

          <ResponsiveDialogFooter className="flex-col gap-2 sm:flex-col">
            {phase === 'resolved' || phase === 'error'
              ? primaryActions()
              : null}
            {phase !== 'resolving' ? (
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => closeLinkResolve()}
              >
                {dict.dismiss ?? 'Close'}
              </Button>
            ) : null}
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>

      {exchangeTarget ? (
        <BusinessCardExchangeDialog
          open={exchangeOpen}
          onOpenChange={(next) => {
            setExchangeOpen(next);
            if (!next) setExchangeTarget(null);
          }}
          targetProfileId={exchangeTarget.profileId}
          targetProfileTitle={exchangeTarget.title}
        />
      ) : null}
    </>
  );
}
